import { Controller } from "@hotwired/stimulus";

/**
 * GO4SINSIGHTS period group (one calendar of the filter bar).
 *
 * Choosing "Personnaliser" in the period select does not submit the filter
 * form: it opens a modal with the "Du" / "Au" dates. "Appliquer" submits
 * only when the end is strictly after the start. Closing the modal without
 * applying puts the previous choice back in the select.
 *
 * A new year or period drops the displayed observation date, so the server
 * observes the situations at the end of the new period (not at the old date).
 */
export default class extends Controller {
  static targets = ["modal", "from", "to", "error", "tooLong"];
  static values = { current: String, maxDays: Number };

  // Runs before the form's auto-submit (the event bubbles through this group).
  periodChanged(event) {
    const input = event.target;
    // The modal's dates only take effect on "Appliquer" — whichever field
    // fired: flatpickr's visible (alt / mobile native) input is not the
    // target input, and typing a date fires "change" on it.
    if (this.modalTarget.contains(input)) {
      event.stopPropagation();
      this.fromChanged();
      return;
    }
    if (!input.name) return;
    if (input.name.endsWith("period") && input.value === "custom") {
      event.stopPropagation();
      this.open();
      return;
    }
    if (input.name.endsWith("year") || input.name.endsWith("period")) this.resetAsOf();
  }

  resetAsOf() {
    const asOf = this.element.querySelector("input[name$='as_of']");
    if (asOf) asOf.value = "";
  }

  open() {
    this.errorTarget.hidden = true;
    if (this.hasTooLongTarget) this.tooLongTarget.hidden = true;
    this.modalController?.open();
  }

  apply() {
    const from = this.fromTarget.value;
    const to = this.toTarget.value;
    if (!from || !to || to <= from) {
      this.errorTarget.hidden = false;
      return;
    }
    // The server ignores a longer range (whole year instead): said here.
    const days = (Date.parse(to) - Date.parse(from)) / 86400000;
    if (this.maxDaysValue && days > this.maxDaysValue && this.hasTooLongTarget) {
      this.errorTarget.hidden = true;
      this.tooLongTarget.hidden = false;
      return;
    }
    this.applying = true;
    this.periodInput.value = "custom";
    this.resetAsOf();
    this.element.closest("form").requestSubmit();
  }

  cancel() {
    this.modalController?.close();
    if (!this.applying) this.restoreSelect();
  }

  // The DS modal also closes on Escape / backdrop: restore there too.
  modalClosed() {
    if (!this.applying) this.restoreSelect();
  }

  restoreSelect() {
    const select = this.periodInput?.closest("[data-controller~='ds-select']");
    if (!select || this.periodInput.value === this.currentValue) return;

    const option = select.querySelector(`[data-value='${this.currentValue}']`);
    this.periodInput.value = this.currentValue;
    if (option) {
      select.querySelector("[data-ds-select-target='label']").textContent = option.dataset.label;
      select.querySelectorAll("[data-ds-select-target='option']")
        .forEach((o) => o.setAttribute("aria-selected", String(o === option)));
    }
  }

  // In the calendar too, the end date starts the day after the start date.
  fromChanged() {
    const picker = this.toTarget._flatpickr;
    const start = this.fromTarget._flatpickr?.selectedDates[0];
    if (!picker || !start) return;

    const next = new Date(start);
    next.setDate(next.getDate() + 1);
    picker.set("minDate", next);
  }

  get periodInput() {
    return this.element.querySelector("input[type='hidden'][name$='period']");
  }

  get modalController() {
    const root = this.modalTarget.querySelector("[data-controller~='ds-modal']");
    return root && this.application.getControllerForElementAndIdentifier(root, "ds-modal");
  }
}
